import { useCallback, useSyncExternalStore } from "react";
import { toast } from "@/hooks/use-toast";
import {
  TEMPLATE_STORAGE_KEY,
  TemplateRecord,
  TemplateSummary,
  deleteTemplate,
  getTemplate,
  listTemplates,
  saveTemplate,
} from "../services/template-storage";

type Listener = () => void;
const listeners = new Set<Listener>();

function notifyLocal() {
  listeners.forEach((l) => l());
}

function subscribe(listener: Listener): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === TEMPLATE_STORAGE_KEY) listener();
  };
  window.addEventListener("storage", onStorage);
  listeners.add(listener);
  return () => {
    window.removeEventListener("storage", onStorage);
    listeners.delete(listener);
  };
}

let cachedSnapshot: TemplateSummary[] = [];
let cachedSnapshotKey = "";

function getSnapshot(): TemplateSummary[] {
  const next = listTemplates();
  const key = next.map((t) => `${t.id}:${t.updatedAt}`).join("|");
  if (key !== cachedSnapshotKey) {
    cachedSnapshot = next;
    cachedSnapshotKey = key;
  }
  return cachedSnapshot;
}

function getServerSnapshot(): TemplateSummary[] {
  return [];
}

export function useTemplates() {
  const templates = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const save = useCallback(
    (input: { id?: string; name: string; projectData: unknown }) => {
      const saved = saveTemplate(input);
      notifyLocal();
      toast({
        title: input.id ? "Template updated" : "Template saved",
        description: saved.name,
      });
      return saved;
    },
    [],
  );

  const remove = useCallback((id: string) => {
    const existing = getTemplate(id);
    deleteTemplate(id);
    notifyLocal();
    toast({
      title: "Template deleted",
      description: existing?.name,
    });
  }, []);

  const load = useCallback((id: string): TemplateRecord | null => {
    return getTemplate(id);
  }, []);

  return { templates, save, remove, load };
}
